import {assert} from 'Asserts';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPipelineType, PipelineType} from 'pipelines/PipelineTypes';

import isPipelineGraphQLInternallyConsistent from './isPipelineGraphQLInternallyConsistent';
import {
  GET_PIPELINE_QUERY_DATA_BY_PIPELINE_TYPE,
  GET_PIPELINE_QUERY_VARIABLES_BY_PIPELINE_TYPE,
  PIPELINE_TYPE_TO_QUERY,
  PipelineQueryData,
  PipelineQueryResult,
  PipelineQueryVariables,
} from './PollingConstants';

/**
 * Refresh an analysis or analysis template GraphQL query.
 */
export async function refreshPipeline<T extends PipelineType = PipelineType>(
  pipelineId: string
): Promise<PipelineQueryData[T]> {
  const pipelineType = getPipelineType(pipelineId) as T;
  const result = await getApolloClient().query<PipelineQueryResult[T], PipelineQueryVariables[T]>({
    query: PIPELINE_TYPE_TO_QUERY[pipelineType],
    variables: GET_PIPELINE_QUERY_VARIABLES_BY_PIPELINE_TYPE[pipelineType](pipelineId),
    fetchPolicy: 'network-only',
    /**
     * Do not let this forced refresh be de-duplicated against an in-flight auto-refresh, because the
     * in-flight auto-refresh may not contain the full updated data for which this forced refresh was
     * triggered.
     */
    context: {queryDeduplication: false},
  });
  const pipeline = GET_PIPELINE_QUERY_DATA_BY_PIPELINE_TYPE[pipelineType](result.data);
  assert(!isInaccessible(pipeline), 'Tried to refresh an inaccessible analysis');

  if (!isPipelineGraphQLInternallyConsistent(pipeline)) {
    return refreshPipeline<T>(pipelineId);
  }

  return pipeline;
}

export const refreshAnalysis = refreshPipeline<PipelineType.ANALYSIS>;

export const refreshAnalysisTemplate = refreshPipeline<PipelineType.ANALYSIS_TEMPLATE>;
